import { Component } from '@theme/component';

/**
 * @typedef {Object} SocialProofCarouselRefs
 * @property {HTMLElement} track
 * @property {HTMLElement} trackWrapper
 * @property {HTMLButtonElement} prevBtn
 * @property {HTMLButtonElement} nextBtn
 */

/** @extends {Component<SocialProofCarouselRefs>} */
class SocialProofCarousel extends Component {
  #index = 0;
  #ro = null;
  #scrollRaf = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#ro = new ResizeObserver(() => this.#goTo(this.#index, false));
    if (this.refs.trackWrapper) {
      this.#ro.observe(this.refs.trackWrapper);
      this.refs.trackWrapper.addEventListener('scroll', () => this.#onScroll(), { passive: true });
    }
    this.#goTo(0, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ro?.disconnect();
    cancelAnimationFrame(this.#scrollRaf);
  }

  handlePrev() {
    this.#goTo(this.#index - 1);
  }

  handleNext() {
    this.#goTo(this.#index + 1);
  }

  #stepWidth() {
    const card = this.refs.track?.querySelector('.sp-card');
    if (!card) return 0;
    const gap = parseFloat(getComputedStyle(this.refs.track).gap) || 20;
    return card.getBoundingClientRect().width + gap;
  }

  #total() {
    return this.refs.track?.querySelectorAll('.sp-card').length ?? 0;
  }

  #visible() {
    const w = this.refs.trackWrapper?.getBoundingClientRect().width ?? 0;
    const step = this.#stepWidth();
    return step > 0 ? Math.round(w / step) : 3;
  }

  #max() {
    return Math.max(0, this.#total() - this.#visible());
  }

  #goTo(index, smooth = true) {
    this.#index = Math.max(0, Math.min(index, this.#max()));
    this.refs.trackWrapper?.scrollTo({
      left: this.#index * this.#stepWidth(),
      behavior: smooth ? 'smooth' : 'instant',
    });
    this.#updateButtons();
  }

  #onScroll() {
    cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => {
      const wrapper = this.refs.trackWrapper;
      if (!wrapper) return;
      const step = this.#stepWidth();
      const atEnd = wrapper.scrollLeft + wrapper.offsetWidth >= (this.refs.track?.scrollWidth ?? 0) - 2;
      this.#index = atEnd
        ? this.#max()
        : step > 0 ? Math.round(wrapper.scrollLeft / step) : 0;
      this.#updateButtons();
    });
  }

  #updateButtons() {
    if (this.refs.prevBtn) this.refs.prevBtn.disabled = this.#index === 0;
    if (this.refs.nextBtn) this.refs.nextBtn.disabled = this.#index >= this.#max();
  }
}

if (!customElements.get('social-proof-carousel')) {
  customElements.define('social-proof-carousel', SocialProofCarousel);
}
